<script setup lang="ts">
import moment from "moment"
import { TuTable, TuTableHeader, TuTableRow } from "@tinyurldev/tu-ui-vue"
import { breakpointsBootstrapV5, useBreakpoints } from "@vueuse/core";

import type { ApiToken } from "@Modules/teams/Data/Models/ApiToken"
import type { Team } from "@Modules/teams/Data/Models/TeamModel"
import useTranslation from "@Composables/useTranslation"
import OrgApiActionDropdown from "@Modules/teams/components/OrgApiActionDropdown.vue"

//
// Props
interface Props {
    team: Team
    tokens: ApiToken[]
    loading?: boolean
    disabled?: boolean
    editToken?: (token: ApiToken) => void
    deleteToken?: (token: ApiToken) => void
}

const props = withDefaults(defineProps<Props>(), {
    loading: true,
    disabled: false,
    editToken: () => {},
    deleteToken: () => {},
});

const { __ } = useTranslation()
const breakpoints = useBreakpoints(breakpointsBootstrapV5);
const isMobileView = breakpoints.smallerOrEqual("xl");

//
// Methods
function formatDate(date: string | null): string {
  if (!date) {
    return __("organization_api_settings.manage_tokens.table_never_used");
  }

  return moment.utc(date).local().format("MMMM Do, YYYY h:mm A")
}
</script>

<template>
  <TuTable
    class="org-api-tokens-table"
    without-scroll
  >
    <template
      v-if="!isMobileView"
      #header
    >
      <TuTableHeader>
        <th>{{ __('organization_api_settings.manage_tokens.table_name') }}</th>
        <th>{{ __('organization_api_settings.manage_tokens.table_date_created') }}</th>
        <th>{{ __('organization_api_settings.manage_tokens.table_last_used') }}</th>
        <th class="org-api-tokens-table__action-cell">
          {{ __('organization_api_settings.manage_tokens.table_action') }}
        </th>
      </TuTableHeader>
    </template>

    <TuTableRow
      v-if="loading"
      class="_loading"
    >
      <td colspan="4">
        <i class="fa fa-spinner fa-spin" />
        {{ __('organization_api_settings.manage_tokens.table_loading') }}
      </td>
    </TuTableRow>

    <TuTableRow v-else-if="!tokens.length">
      <td
        class="_no_tokens text-center"
        colspan="4"
      >
        <i class="fa-solid fa-circle-exclamation" />
        {{ __('organization_api_settings.manage_tokens.table_no_tokens') }}
      </td>
    </TuTableRow>

    <template v-else-if="!isMobileView">
      <TuTableRow
        v-for="token in tokens"
        :key="token.id"
        class="_token_row"
      >
        <td class="org-api-tokens-table__token-name _token_name">
          {{ token.name }}
        </td>
        <td class="_token_created">
          {{ formatDate(token.created_at) }}
        </td>
        <td class="_token_last_used">
          {{ formatDate(token.last_used_at) }}
        </td>
        <td class="org-api-tokens-table__action-cell">
          <OrgApiActionDropdown
            v-if="!props.disabled"
            class="_token_actions"
            @edit="editToken(token)"
            @delete="deleteToken(token)"
          />
        </td>
      </TuTableRow>
    </template>

    <template v-else>
      <TuTableRow
        v-for="token in tokens"
        :key="token.id"
        class="_token_row"
      >
        <td colspan="4">
          <div class="org-api-tokens-table__cell-mobile">
            <p class="org-api-tokens-table__cell-mobile-title">
              {{ __('organization_api_settings.manage_tokens.table_name') }}
            </p>
            <p class="org-api-tokens-table__cell-mobile-content org-api-tokens-table__token-name">
              {{ token.name }}
            </p>
          </div>
          <div class="org-api-tokens-table__cell-mobile">
            <p class="org-api-tokens-table__cell-mobile-title">
              {{ __('organization_api_settings.manage_tokens.table_date_created') }}
            </p>
            <p class="org-api-tokens-table__cell-mobile-content">
              {{ formatDate(token.created_at) }}
            </p>
          </div>
          <div class="org-api-tokens-table__cell-mobile">
            <p class="org-api-tokens-table__cell-mobile-title">
              {{ __('organization_api_settings.manage_tokens.table_last_used') }}
            </p>
            <p class="org-api-tokens-table__cell-mobile-content _token_last_used">
              {{ formatDate(token.last_used_at) }}
            </p>
          </div>
          <div class="org-api-tokens-table__cell-mobile">
            <p class="org-api-tokens-table__cell-mobile-title">
              {{ __('organization_api_settings.manage_tokens.table_action') }}
            </p>
            <div class="org-api-tokens-table__cell-mobile-content">
              <OrgApiActionDropdown
                v-if="!props.disabled"
                class="_token_actions"
                @edit="editToken(token)"
                @delete="deleteToken(token)"
              />
            </div>
          </div>
        </td>
      </TuTableRow>
    </template>
  </TuTable>
</template>

<style lang="scss" scoped>
@use "@tinyurldev/tu-ui-vue/scss/settings/spacing.scss" as spacing;
@use "@tinyurldev/tu-ui-vue/scss/settings/typography.scss" as typography;

.org-api-tokens-table {
  &__token-name {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__action-cell {
    width: 10%;
    white-space: nowrap;
  }

  &__cell-mobile {
    display: grid;
    grid-template-columns: 1fr 1.5fr;
    gap: spacing.$xxxs;

    &:not(:last-child) {
      margin-bottom: spacing.$xxxs;
    }
  }

  &__cell-mobile-title {
    font-weight: typography.$font-weight-bold;
  }

  &__cell-mobile-title,
  &__cell-mobile-content {
    margin: 0;
  }
}
</style>
